import { IPdfImages } from '../types/index';
import { isPerfSampled, perfMeasure } from '../utils/perfMetrics';
import { checkIsAuthCDNUrl } from '@umu/umu-util';

/**
 * 图片加载队列
 */
export interface ImageLoadQueueOptions {
    maxParallelDownloads?: number;
    maxCacheSize?: number;
}

export class ImageLoadQueue {
    static MAX_OF_PARALLEL_DOWNLOADS = 5; // 最大并行下载数
    static MAX_CACHE_SIZE = 12; // 最大缓存数量（解码后的 Image 对象，避免过大占用）
    path: string = '';
    list: string[] = [];
    loadedQueue: Set<string> = new Set();
    loadingQueue: Set<string> = new Set();
    private cache: Map<string, HTMLImageElement> = new Map();
    private inflight: Map<string, Promise<HTMLImageElement>> = new Map();
    private maxParallelDownloads: number;
    private maxCacheSize: number;

    constructor(data: IPdfImages = { path: '', list: [] }, options?: ImageLoadQueueOptions) {
        this.path = data.path; // 图片路径
        this.list = data.list; // 图片列表
        this.maxParallelDownloads = Math.max(
            1,
            Number(options?.maxParallelDownloads || ImageLoadQueue.MAX_OF_PARALLEL_DOWNLOADS),
        );
        this.maxCacheSize = Math.max(1, Number(options?.maxCacheSize || ImageLoadQueue.MAX_CACHE_SIZE));
    }

    private getUrlByIndex(imageIndex: number): string | null {
        if (!this.list || this.list.length === 0) return null;
        const index = Math.max(0, Math.min(imageIndex, this.list.length - 1));
        const key = this.list[index];
        if (!key) return null;
        return `${this.path}${key}`;
    }

    private touchCache(url: string, img: HTMLImageElement): void {
        // LRU：删除再插入，保证最近使用在末尾
        this.cache.delete(url);
        this.cache.set(url, img);
        if (this.cache.size <= this.maxCacheSize) {
            return;
        }
        const first = this.cache.keys().next();
        if (!first.done) {
            this.cache.delete(first.value);
        }
    }

    /**
     * 加载并解码图片（in-flight 去重 + LRU 缓存）
     */
    load(imageIndex: number): Promise<HTMLImageElement> {
        const url = this.getUrlByIndex(imageIndex);
        if (!url) {
            return Promise.reject(new Error('invalid image url'));
        }

        const cached = this.cache.get(url);
        if (cached) {
            this.touchCache(url, cached);
            if (isPerfSampled()) {
                perfMeasure('image_cache_hit', 1, 'all');
            }
            return Promise.resolve(cached);
        }

        const existed = this.inflight.get(url);
        if (existed) {
            if (isPerfSampled()) {
                perfMeasure('image_inflight_hit', 1, 'all');
            }
            return existed;
        }
        if (isPerfSampled()) {
            perfMeasure('image_cache_miss', 1, 'all');
        }

        const p = new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            // 部分资源域名需要带 cookie/凭证，否则 Safari 会 403 或触发异常行为
            img.crossOrigin = checkIsAuthCDNUrl(url) ? 'use-credentials' : 'anonymous';

            const cleanup = () => {
                img.onload = null;
                img.onerror = null;
            };

            // Safari 下偶发出现 onload/onerror 都不触发导致 promise 永久 pending（从而页面一直空白）
            // 加一个超时兜底，避免永远卡住
            const timeoutMs = 15_000;
            const timer = setTimeout(() => {
                cleanup();
                reject(new Error(`image load timeout: ${url}`));
            }, timeoutMs);

            img.onload = () => {
                clearTimeout(timer);
                cleanup();
                this.touchCache(url, img);
                resolve(img);
            };
            img.onerror = () => {
                clearTimeout(timer);
                cleanup();
                reject(new Error(`image load failed: ${url}`));
            };
            img.src = url;

            // decode() 可把解码过程变为 Promise（若支持），更利于避免同步阻塞
            // 注意：decode 失败不应该阻断 onload/onerror 路径
            if (typeof (img as any).decode === 'function') {
                (img as any)
                    .decode()
                    .then(() => {
                        // decode 成功后仍会走 onload；这里不 resolve，避免双触发
                    })
                    .catch(() => {
                        // ignore
                    });
            }
        }).finally(() => {
            this.inflight.delete(url);
        });

        this.inflight.set(url, p);
        return p;
    }

    preload(imageIndex: number): void {
        if (!this.list || this.list.length === 0) {
            return;
        }
        // Safari 下并发过多图片请求更容易出现“永远 pending”，这里收敛预加载并发，优先保证当前页可见
        if (this.inflight.size >= this.maxParallelDownloads) {
            return;
        }
        const idx = Math.max(0, Math.min(imageIndex, this.list.length - 1));
        const imageKey = this.list[idx];
        if (!imageKey) {
            return;
        }
        const url = this.getUrlByIndex(idx);
        if (!url) {
            return;
        }
        if (this.loadedQueue.has(imageKey) || this.loadingQueue.has(imageKey)) {
            return;
        }
        this.loadingQueue.add(imageKey);
        this.load(idx)
            .then(() => {
                this.loadedQueue.add(imageKey);
            })
            .catch(() => {
                // ignore
            })
            .finally(() => {
                this.loadingQueue.delete(imageKey);
            });
    }

    batchPreload(startIndex: number, count: number, dir: 'pre' | 'next'): void {
        if (!this.list || this.list.length === 0) {
            return;
        }
        if (count <= 0) {
            return;
        }
        if (this.inflight.size >= this.maxParallelDownloads) {
            return;
        }
        let newlyAdd = 0;
        const start = Math.max(0, Math.min(startIndex, this.list.length - 1));

        if (dir === 'next') {
            for (let index = start; index < this.list.length; index++) {
                const key = this.list[index];
                if (!key || this.loadedQueue.has(key)) {
                    continue;
                }
                this.preload(index);
                newlyAdd += 1;
                if (newlyAdd === count) {
                    break;
                }
            }
            return;
        }

        // dir === 'pre'
        for (let index = start; index >= 0; index--) {
            const key = this.list[index];
            if (!key || this.loadedQueue.has(key)) {
                continue;
            }
            this.preload(index);
            newlyAdd += 1;
            if (newlyAdd === count) {
                break;
            }
        }
    }

    /**
     * 以当前页为中心进行窗口预热（优先预热更可能访问方向）
     */
    preloadAround(centerIndex: number, dir: 'pre' | 'next', preCount = 2, nextCount = 3): void {
        if (!this.list || this.list.length === 0) {
            return;
        }
        if (this.inflight.size >= this.maxParallelDownloads) {
            return;
        }
        const available = this.maxParallelDownloads - this.loadingQueue.size;
        if (available <= 0) {
            return;
        }

        // 根据方向调整预算：更可能访问方向给更多名额
        const preBudget = dir === 'pre' ? Math.min(preCount + 1, available) : Math.min(preCount, available);
        const nextBudget = dir === 'next' ? Math.min(nextCount + 1, available) : Math.min(nextCount, available);

        // 先预热更可能方向
        if (dir === 'next') {
            this.batchPreload(centerIndex + 1, nextBudget, 'next');
            this.batchPreload(centerIndex - 1, preBudget, 'pre');
        } else {
            this.batchPreload(centerIndex - 1, preBudget, 'pre');
            this.batchPreload(centerIndex + 1, nextBudget, 'next');
        }

        if (isPerfSampled()) {
            perfMeasure('image_preload_budget', preBudget + nextBudget, 'all');
        }
    }

    /**
     * 进入排队状态
     * @param startIndex
     */
    enqueue(startIndex: number, dir: 'pre' | 'next'): void {
        if (startIndex === this.list.length - 1) {
            return;
        }
        const count = this.maxParallelDownloads - this.loadingQueue.size;
        if (count === 0) {
            return;
        }

        // 保持旧行为：从当前位置向单方向预热
        this.batchPreload(startIndex, count, dir);
    }
}
